import { useState } from "react";
import { BigNumber } from "bignumber.js";
import { getLocalDateFullMonthText } from "@merchstores/admin/components/DateTime";
import { CTA } from "@merchstores/shared/elements/Cta";
import { OrderStatus } from "@merchstores/admin/components/MerchStoreOrders";
import { Role } from "@merchstores/admin/context/Roles";
import { IMerchStoreOrder } from "@merchstores/admin/components/MerchStoreOrders";
import { IVariantQuantityDataEntry } from "./OrderIndividualDetailsPage";

import "./styles.scss";

import OrderCalculator from "@merchstores/admin/components/MerchStoreOrders/OrderCalculator";
import { moneyFormat } from "@merchstores/shared/components/Money";
import { useEffect } from "react";

export interface IOrderDetailsSummaryProps {
  order: IMerchStoreOrder;
  individualOrder: any;
  userRole: Role;
  closeDate: string;
  updateOrderDetails: () => void;
  variantQuantityEditActive: boolean;
  variantQuantityData: IVariantQuantityDataEntry[];
  canEditOrder: boolean;
}

export const OrderDetailsSummary: React.FC<IOrderDetailsSummaryProps> = (props: IOrderDetailsSummaryProps) => {
  const { order, individualOrder, closeDate, updateOrderDetails, variantQuantityEditActive, variantQuantityData, canEditOrder } = props;
  const [taxRate, setTaxRate] = useState("")
  const [itemCount, setItemCount] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [total, setTotal] = useState("");
  const [shipping, setShipping] = useState("");
  const [tax, setTax] = useState("");
  
  useEffect(() => {
    if (individualOrder && individualOrder.currentTotalPriceSet) {
      const orderSubtotalPrice = new BigNumber(individualOrder.currentSubtotalPriceSet.presentmentMoney.amount);
      setSubtotal(orderSubtotalPrice.toString());
      const orderTotalShipping = new BigNumber(individualOrder.totalShippingPriceSet.presentmentMoney.amount);
      setShipping(orderTotalShipping.toString());
      const orderTotalTax = new BigNumber(individualOrder.currentTotalTaxSet.presentmentMoney.amount);
      setTax(orderTotalTax.toString());
      const priceBeforeTax = orderSubtotalPrice.plus(orderTotalShipping);
      const taxRate = orderTotalTax.dividedBy(priceBeforeTax).multipliedBy(100).toString()
      setTaxRate(taxRate);
      setTotal(individualOrder.currentTotalPriceSet.presentmentMoney.amount)
      let itemTotal = 0;
      individualOrder.lineItems.forEach((lineItem: any) => {
        itemTotal += lineItem.currentQuantity;
      })
      setItemCount(itemTotal)
    }
  }, [individualOrder])

  useEffect(() => {
    let newItemCount = 0;
    let newSubtotal = new BigNumber("0");
    const currentShipping = new BigNumber(shipping);
    const currentTax = new BigNumber(taxRate).dividedBy(100);
    variantQuantityData.forEach((variant) => {
      newItemCount += variant.quantity;
      const variantPrice = new BigNumber(variant.price);
      const variantSubtotal = variantPrice.multipliedBy(variant.quantity)
      newSubtotal = newSubtotal.plus(variantSubtotal);
    })
    const newTax = newSubtotal.plus(currentShipping).times(currentTax)
    const newTotal = newSubtotal.plus(currentShipping).plus(newTax).toFormat(2);
    setTotal(newTotal);
    setSubtotal(newSubtotal.toFormat(2));
    setTax(newTax.toFormat(2));
    setItemCount(newItemCount);
  }, [variantQuantityData])

  const isClosed = order.status === OrderStatus.closed;

  return (
    <div className="order-details-sidebar-menu p-8 bg-gray-50 lg:min-w-order-sidebar flex flex-col items-start rounded-tr-24 rounded-br-24">
      <div className="w-full">
        <div className="w-full text-sm text-merch-dark-gray font-bold mb-5">
          <div className='title w-full pb-20'>Payment Details</div>
        </div>
        <div className='flex grid grid-cols-3 gap-4 pb-30 mb-30 border-b item-text'>
          <div className='font-bold'>Subtotal:</div>
          <div>{`${itemCount} Items`}</div>
          <div className='text-right item-value'>${subtotal}</div>
          <div className='font-bold'>Shipping:</div>
          <div>{order.shippingTitle}</div>
          <div className='text-right item-value'>${shipping}</div>
          <div className='font-bold'>Sales Tax:</div>
          <div></div>
          <div className='text-right item-value'>${tax}</div>
        </div>
        <div className='flex justify-between mb-25 item-text item-value font-bold'>
          <div>Total:</div>
          <div className='text-lg'>{ moneyFormat(total) }</div>
        </div>
        {canEditOrder && (
          <div className="flex w-full">
            <CTA
              disabled={!variantQuantityEditActive}
              formSubmit={false}
              size="standard"
              type="primary"
              classes='w-full'
              onClick={updateOrderDetails}>
              Update Order
            </CTA>
          </div>
        )}
        <div className='flex justify-center flex-col text-center mt-20'>
          <div className='item-text mb-10'>{isClosed ? 'This group order was processed on:' : 'This group order will process automatically on:'}</div>
          <div className='text-lg font-bold item-value'>{getLocalDateFullMonthText(closeDate)}</div>
        </div>
      </div>
    </div>
  )
}